import { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiChip,
  MiDetailPageHeader,
  MiIcon,
  MiMenu,
} from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { mdiChevronDown } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { StepperView } from "../../components/StepperView/StepperView";
import { AreaTemplateModal } from "./AreaTemplateModal/AreaTemplateModal";
import { BidDetails } from "./BidDetails/BidDetails";
import { ConfigureBid } from "./ConfigureBid/ConfigureBid";
import { CreateBidProvider, useCreateBidContext } from "./CreateBidContext";
import { STEP, STEPS } from "./constants";

function CreateBidContent() {
  const [openAreaTemplateModal, setOpenAreaTemplateModal] = useState(false);

  const { params, navigate } = useRouter();
  const { bidId } = params;
  const { classes, theme } = useStyles();
  const {
    isLoading,
    form,
    activeStep,
    fetchBidDetails,
    setActiveStep,
    handleCreateBid,
    communities,
    getCommunities,
    accounts,
    getAccounts,
    isConfigStepValid,
  } = useCreateBidContext();
  const {
    formState: { isValid, isDirty },
    watch,
    getValues,
  } = form;

  const { name, communityId, builderId, additionalFeatures, planAreas } =
    watch();

  useEffect(() => {
    if (bidId) {
      fetchBidDetails(bidId);
    }
    getCommunities();
    getAccounts();
  }, []);

  const ACTIVE_STEP_CONFIG = useMemo(
    () => ({
      [STEP.DETAILS]: {
        disabled: !isValid,
      },
      [STEP.CONFIGURE]: {
        disabled: !isConfigStepValid,
      },
      [STEP.OPTIONS]: {
        disabled: !isValid,
      },
      [STEP.REQUIREMENTS]: {
        disabled: !isValid,
      },
    }),
    [isValid, isConfigStepValid]
  );

  const selectedConfig = ACTIVE_STEP_CONFIG[activeStep] || {};

  const customerName = accounts.find((a) => a.id === builderId)?.name;
  const communityName = communities.find((c) => c.id === communityId)?.name;

  const regex = /baths?/i;
  const numberOfBaths = planAreas?.filter((a) => regex.test(a.label || ""));
  const numberOfFloors = [...new Set(planAreas.map((a) => a.floorId))];
  const firstFloorId = numberOfFloors[0];
  const numberOfUps = planAreas.filter(
    (a) => regex.test(a.label || "") && a.floorId !== firstFloorId
  );

  const handleSaveBid = async () => {
    const data = getValues();
    await handleCreateBid(data);
    if (!bidId) {
      navigate(data.id);
    }
  };

  const goBack = () => navigate("/estimating/bids");

  return (
    <>
      <MiDetailPageHeader
        title={
          <>
            Create Bid
            {(name || communityId || builderId) && (
              <span className={classes.titleSpan}>
                :{" "}
                {[customerName || "Customer", communityName, name || "Bid Name"]
                  .filter(Boolean)
                  .join(" | ")}
              </span>
            )}{" "}
            <MiChip className={classes.setupChip} label="Setup" />
          </>
        }
        actionComponent={
          <Box className={classes.actionBox}>
            {planAreas.length > 0 && (
              <Typography className={classes.actionText} variant="h5">
                {numberOfBaths.length} Baths
                {" - "}
                {numberOfFloors.length} Story
                {" - "}
                {numberOfUps.length} Up
                {" - "}
              </Typography>
            )}
            <Typography className={classes.actionText} variant="h5">
              {additionalFeatures}
            </Typography>
            <Box sx={{ display: "flex" }}>
              <MiButton
                title="Save"
                variant={BUTTON_VARIANTS.CONTAINED}
                disabled={!isDirty || !isValid}
                onClick={handleSaveBid}
                sx={{ borderRadius: "4px 0px 0px 4px" }}
              />
              <Box className={classes.menuBox}>
                <MiMenu
                  data={[
                    {
                      label: "Save Area Template",
                      action: () => setOpenAreaTemplateModal(true),
                    },
                    {
                      label: "Save & Close",
                      disabled: !isDirty || !isValid,
                      action: async () => {
                        await handleSaveBid();
                        goBack();
                      },
                    },
                  ]}
                  optionalElement={
                    <MiIcon
                      path={mdiChevronDown}
                      color={theme.palette.common.white}
                    />
                  }
                  optionalElementStyle={{
                    display: "flex",
                    alignItems: "center",
                  }}
                />
              </Box>
            </Box>
          </Box>
        }
        actionContainerStyle={{
          display: "flex",
          flex: 2,
          justifyContent: "flex-end",
        }}
        style={{
          backgroundColor: theme.palette.common.white,
        }}
        loading={isLoading}
        customOnBack={goBack}
      />
      <StepperView
        steps={STEPS}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        disabled={selectedConfig.disabled}
        disableNavigation
      >
        {activeStep === STEP.DETAILS && <BidDetails />}
        {activeStep === STEP.CONFIGURE && <ConfigureBid />}
        {activeStep === STEP.OPTIONS && <>placeholder</>}
        {activeStep === STEP.REQUIREMENTS && <>placeholder</>}
      </StepperView>
      <AreaTemplateModal
        open={openAreaTemplateModal}
        onClose={() => setOpenAreaTemplateModal(false)}
      />
    </>
  );
}

export function CreateBid() {
  return (
    <CreateBidProvider>
      <CreateBidContent />
    </CreateBidProvider>
  );
}

const useStyles = makeStyles()((theme) => ({
  titleSpan: {
    color: theme.palette.primary.main,
  },
  actionBox: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  actionText: {
    color: theme.palette.primary.main,
    fontWeight: theme.fontWeight.semiBold,
  },
  menuBox: {
    backgroundColor: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    borderRadius: "0px 4px 4px 0px",
    borderLeft: `1px solid ${theme.palette.grey.accent}`,
    cursor: "pointer",
  },
  setupChip: {
    border: `1px solid ${theme.palette.caution.dark}`,
    color: theme.palette.caution.dark,
    backgroundColor: theme.palette.yellow.lightAccent,
    borderRadius: 4,
    height: 18,
    fontWeight: theme.fontWeight.regular,
  },
}));
